import { Button, Card, Label, Spinner, TextInput } from "flowbite-react";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { httpService } from "../../../services/httpService";
import { API_CONFIG } from "../../../shared/constants";
import { IllustrationForgotPassword } from "../../../assets/Illustrations";
import brandLogo from "../../../assets/images/Logo.png";
import { Envelope } from "@phosphor-icons/react";

export interface IForgotPasswordResponse {
  message: string;
  uidb64: string;
  token: string;
  reset_url: string;
}

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError(t("auth.login.emailRequired"));
      return;
    }

    if (!validateEmail(email)) {
      setError(t("auth.invalidEmail"));
      return;
    }

    setIsLoading(true);
    try {
      httpService
        .post<{ message: string }>(API_CONFIG.path.forgotPassword, {
          email,
        })
        .then(({ message }) => {
          toast.success(message);
          // if (data) {
          //   navigate(`/reset-password?uidb64=${data.uidb64}&token=${data.token}`);
          // }
          setIsLoading(false);
          setIsSubmitted(true);
        })
        .catch((err) => {
          setIsLoading(false);
          toast.error(err.message);
        });
    } catch (error) {
      setIsLoading(false);
      console.log(" handleSubmit error:", error);
      toast.error(t("auth.forgotPasswordError"));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-200 via-indigo-100 to-white relative overflow-hidden">
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center mb-4 md:mb-8 animate-fade-in">
          <img
            src={brandLogo}
            alt="Logo"
            className="h-10 md:h-16 mb-2 md:mb-4 cursor-pointer"
            onClick={() => navigate("/")}
          />
        </div>

        <Card
          className="w-full relative z-10 shadow-2xl border-none bg-white/90 backdrop-blur-sm animate-fade-in-up"
          theme={{
            root: {
              children:
                "flex min-h-[400px] flex-col justify-center gap-4 p-3 md:p-6",
            },
          }}
        >
          <div className="flex flex-col md:flex-row">
            {/* Left side - Illustration - Hidden on mobile */}
            <div className="hidden md:flex w-1/2 p-8 items-center justify-center border-r border-gray-200 animate-fade-in-left">
              <div className="w-64 h-64">
                <IllustrationForgotPassword
                  scale={1.5}
                  className="w-full h-full"
                />
              </div>
            </div>

            {/* Right side - Form */}
            <div className="w-full md:w-1/2 p-4 md:p-8 md:pl-12 flex flex-col justify-center animate-fade-in-right">
              <div className="text-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {t("auth.forgotPassword")}
                </h2>
                <p className="text-sm text-gray-600">
                  {isSubmitted
                    ? t("auth.resetPasswordEmailSent")
                    : t("auth.forgotPasswordDescription")}
                </p>
              </div>

              {!isSubmitted ? (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4 md:space-y-6"
                >
                  <div>
                    <Label
                      htmlFor="email"
                      value={t("common.email")}
                      className="text-gray-700"
                    />
                    <TextInput
                      id="email"
                      type="email"
                      icon={Envelope}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(undefined);
                      }}
                      placeholder="name@company.com"
                      color={error ? "failure" : undefined}
                      helperText={error}
                      className="mt-1"
                    />
                  </div>

                  <Button
                    type="submit"
                    color="indigo"
                    className="w-full transition-all duration-200 hover:scale-[1.02]"
                    disabled={isLoading}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {isLoading && <Spinner color={"purple"} size={"sm"} />}
                      {isLoading
                        ? t("common.processing")
                        : t("auth.sendResetPasswordLink")}
                    </div>
                  </Button>
                </form>
              ) : (
                <div>
                  <Button
                    as={Link}
                    to="/auth"
                    color="light"
                    className="w-full transition-all duration-200 hover:scale-[1.02]"
                  >
                    {t("auth.backToLogin")}
                  </Button>
                </div>
              )}

              {!isSubmitted && (
                <div className="mt-4 text-center">
                  <Link
                    to="/auth"
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
                  >
                    {t("auth.rememberPassword")}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
